/**
 * Analytics Tracking Utility
 * Tracks page views, events, sessions, and user interactions
 * SECURITY: Uses httpOnly cookies for authentication (credentials: 'include')
 */

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Track a page view
 */
export const trackPageView = async (page) => {
  try {
    await fetch(`${API_BASE}/api/analytics/pageview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // SECURITY: Auth via httpOnly cookie
      body: JSON.stringify({
        page,
        referrer: document.referrer || null,
        url: window.location.href
      })
    });
  } catch (err) {
    console.debug('Analytics pageview error:', err);
  }
};

/**
 * Track a custom event
 */
export const trackEvent = async (eventType, eventData = {}) => {
  try {
    await fetch(`${API_BASE}/api/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // SECURITY: Auth via httpOnly cookie
      body: JSON.stringify({
        event_type: eventType,
        event_data: {
          ...eventData,
          session_id: getSessionId(),
          timestamp: new Date().toISOString()
        }
      })
    });
  } catch (err) {
    console.debug('Analytics event error:', err);
  }
};

/**
 * Start an analytics session
 */
export const startSession = async () => {
  try {
    const response = await fetch(`${API_BASE}/api/analytics/session/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // SECURITY: Auth via httpOnly cookie
      body: JSON.stringify({
        landing_page: window.location.pathname,
        referrer: document.referrer || null,
        url: window.location.href
      })
    });
    
    if (response.ok) {
      const data = await response.json();
      sessionStorage.setItem('analytics_session_id', data.session_id);
      return data.session_id;
    }
  } catch (err) {
    console.debug('Analytics session start error:', err);
  }
  return null;
};

/**
 * End the current analytics session
 */
export const endSession = async () => {
  const sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) return;
  
  try {
    await fetch(`${API_BASE}/api/analytics/session/${sessionId}/end`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include' // SECURITY: Auth via httpOnly cookie
    });
  } catch (err) {
    console.debug('Analytics session end error:', err);
  }
};

/**
 * Common event types for easy tracking
 */
export const Events = {
  // Authentication
  LOGIN_START: 'login_start',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  SIGNUP_START: 'signup_start',
  SIGNUP_SUCCESS: 'signup_success',
  LOGOUT: 'logout',
  
  // Journal
  ENTRY_CREATE_START: 'entry_create_start',
  ENTRY_SAVED: 'entry_saved',
  ENTRY_DELETED: 'entry_deleted',
  ENTRY_VIEWED: 'entry_viewed',
  
  // Features
  VOICE_TYPING_START: 'voice_typing_start',
  VOICE_TYPING_COMPLETE: 'voice_typing_complete',
  AI_COACH_REQUESTED: 'ai_coach_requested',
  AI_COACH_RECEIVED: 'ai_coach_received',
  PDF_EXPORT_SINGLE: 'pdf_export_single',
  PDF_EXPORT_ALL: 'pdf_export_all',
  
  // Engagement
  THEME_CHANGED: 'theme_changed',
  PROFILE_UPDATED: 'profile_updated',
  BADGE_EARNED: 'badge_earned',
  STREAK_MILESTONE: 'streak_milestone',
  
  // Navigation
  CALENDAR_DATE_SELECTED: 'calendar_date_selected',
  BANNER_CLICKED: 'banner_clicked',
  
  // Errors
  ERROR_OCCURRED: 'error_occurred'
};

// Auto-track page visibility changes (for session duration)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // User is leaving - try to end session
      endSession();
    }
  });
  
  // Also try to end session on page unload
  window.addEventListener('beforeunload', () => {
    endSession();
  });
}

export default {
  trackPageView,
  trackEvent,
  startSession,
  endSession,
  Events
};
